import React, { useCallback, useState } from 'react';
import { makeStyles, Grid, Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { AddSharp } from '@material-ui/icons';
import classNames from 'classnames';
import { ResourceRequestIcon } from '~/modules/common/components/Icons';
import { MoreMenu, MoreMenuOption } from '~/modules/common/components';
import { FormattedHoursAndCost } from '~/modules/resourcing/common/components';
import {
  CostVarianceNumber,
  HoursVarianceNumber
} from '~/modules/resourcing/common/components/VarianceStyledFormattedNumber';
import AddAllocationEditor from './AddAllocationEditor';

const taskEstimateRowValues = {
  actuals: {
    hours: 2.0,
    cost: [
      {
        amount: 450.0,
        currencySymbol: '$'
      }
    ]
  },
  workRemaining: {
    hours: 8,
    cost: [
      {
        amount: 1800.0,
        currencySymbol: '$'
      }
    ]
  },
  estimate: {
    hours: 10,
    cost: [
      {
        amount: 2250.0,
        currencySymbol: '$'
      }
    ]
  }
};

export const useRowStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 1, 0, 0),
    width: '1.5em',
    height: '100%'
  },
  moreMenu: {
    height: 'min-content',
    alignItems: 'center',
    padding: '0 !important'
  },
  selectorContainer: {
    width: '100%',
    margin: 0,
    justifyContent: 'space-between',
    alignContent: 'center'
  },
  selectors: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing(0.5, 1.5, 0.5, 0)} !important`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
      cursor: 'pointer'
    }
  },
  estimateRowContainer: {
    padding: theme.spacing(1.5, 0, 2)
  },
  containers: {
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0, 1, 2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  },
  estimateContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 3,
    padding: theme.spacing(1, 3)
  },
  estimate: {
    display: 'flex'
  },
  allocationContainer: {
    flexGrow: 4
  },
  titles: {
    color: theme.palette.text.secondary
  },
  variance: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'end'
  }
}));

const useCostHourStyles = makeStyles(theme => ({
  root: {
    textAlign: 'left',
    padding: 0
  },
  hours: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium
  },
  cost: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightRegular
  },
  currency: {
    lineHeight: 1,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular
  }
}));

export const TaskEstimateRow = () => {
  const classes = useRowStyles();
  const costHourClasses = useCostHourStyles();

  const { formatMessage } = useIntl();

  const [anchorEl, setAnchorEl] = useState(null);
  const onOpen = useCallback(event => setAnchorEl(event.target), [setAnchorEl]);

  return (
    <>
      <Grid container className={classes.selectorContainer}>
        <Grid item className={classes.selectors}>
          <ResourceRequestIcon className={classes.icon} />
          <div>
            <Typography variant="subtitle1">
              <FormattedMessage id="taskResourceEstimates.selectAResource" />
            </Typography>
            <Typography variant="subtitle2">
              <FormattedMessage id="taskResourceEstimates.selectARole" />
            </Typography>
          </div>
        </Grid>
        {/* Todo: Update menu options & implement actions */}
        <Grid item component={MoreMenu} className={classes.moreMenu}>
          <MoreMenuOption disabled>
            <Typography variant="body2">
              <FormattedMessage id="taskResourceEstimates.removeResource" />
            </Typography>
          </MoreMenuOption>
        </Grid>
      </Grid>

      <Grid container className={classes.estimateRowContainer} spacing={2}>
        <Grid
          item
          className={classNames(classes.estimateContainer, classes.containers)}
        >
          <div>
            {/* Todo: fetch actual 'Actual' values and display */}
            <Typography variant="subtitle2" className={classes.titles}>
              <FormattedMessage id="taskResourceEstimates.actual" />
            </Typography>
            <FormattedHoursAndCost
              classes={costHourClasses}
              hours={taskEstimateRowValues.actuals.hours}
              hoursSuffix={formatMessage({
                id: 'taskResourceEstimates.hoursSuffix'
              })}
              costs={taskEstimateRowValues.actuals.cost}
              showAbbreviatedValue={false}
            />
          </div>
          <AddSharp />
          <div>
            {/* Todo: fetch actual 'work remaining' values and display */}
            <Typography variant="subtitle2" className={classes.titles}>
              <FormattedMessage id="taskResourceEstimates.workRemaining" />
            </Typography>
            <FormattedHoursAndCost
              classes={costHourClasses}
              hours={taskEstimateRowValues.workRemaining.hours}
              hoursSuffix={formatMessage({
                id: 'taskResourceEstimates.hoursSuffix'
              })}
              costs={taskEstimateRowValues.workRemaining.cost}
              showAbbreviatedValue={false}
            />
          </div>
          <Typography variant="h5">=</Typography>
          <div className={classes.estimate}>
            {/* Todo: fetch actual 'estimate' values and display */}
            {/* Todo: Add "+Estimate" functionality */}
            <div>
              <Typography variant="subtitle2" className={classes.titles}>
                <FormattedMessage id="taskResourceEstimates.estimate" />
              </Typography>
              <FormattedHoursAndCost
                classes={costHourClasses}
                hours={taskEstimateRowValues.estimate.hours}
                hoursSuffix={formatMessage({
                  id: 'taskResourceEstimates.hoursSuffix'
                })}
                costs={taskEstimateRowValues.estimate.cost}
                showAbbreviatedValue={false}
              />
            </div>
            <div className={classes.variance}>
              {/* Todo: Complete variance calculations & conditionally render */}
              <HoursVarianceNumber
                value={1.7}
                showAddIcon
                isRoundedValue={false}
                formattedMessageKey="taskResourceEstimates.hoursSuffix"
              />
              <CostVarianceNumber value={204} prefix="$" showAddIcon />
            </div>
          </div>
        </Grid>

        {/* Todo: fetch actual 'Allocation' values and display */}
        <AddAllocationEditor anchorEl={anchorEl} setAnchorEl={setAnchorEl}>
          <Grid
            item
            className={classNames(
              classes.allocationContainer,
              classes.containers
            )}
            onClick={onOpen}
          >
            <Typography variant="subtitle2" className={classes.titles}>
              <FormattedMessage id="taskResourceEstimates.allocation" />
            </Typography>
            <FormattedMessage id="taskResourceEstimates.addAllocation" />
          </Grid>
        </AddAllocationEditor>
      </Grid>
    </>
  );
};

TaskEstimateRow.propTypes = {};

export default TaskEstimateRow;
